/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownProps } from 'react-markdown';

import { Grid, typography2 } from '@coursera/cds-core';

import ModuleLayout, { getModuleLayoutConfig, noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

export type Props = {
  citations: string | null | undefined;
  /**
   * Optional CSS override applied to the inner Grid item. Intended for
   * consumers that render this module inside a CSS-container layout
   * (e.g. XDP's two-panel chat) and need to override the default `md={6}`
   * sizing when the container is narrow. cds-labs stays agnostic of any
   * specific container.
   */
  containerBasedFullWidthOverride?: SerializedStyles;
} & ModuleLayoutConfig &
  Omit<ReactMarkdownProps, 'source'>;

const styles = {
  // Support for other markup can be added.
  // The reason for restricted support is to prevent deviation from the
  // original intent of the citation module.
  markdown: css`
    p,
    li,
    span {
      ${typography2.bodySecondary}
      color: var(--cds-color-neutral-primary-weak);
      white-space: pre-wrap;
    }
  `,
  markdownInverted: css`
    p,
    li,
    span,
    a {
      ${typography2.bodySecondary}
      color: var(--cds-color-neutral-primary-invert);
      white-space: pre-wrap;
    }
  `,
};

type MarkdownProps = Omit<ReactMarkdownProps, 'source'> & {
  source: string | null | undefined;
  isInverted: boolean;
};

function Markdown(props: MarkdownProps) {
  const { renderers, source, isInverted } = props;

  if (!source) {
    return null;
  }

  const customRenderers = {
    ...renderers,
  };

  return (
    <ReactMarkdown
      renderers={customRenderers}
      {...props}
      source={source}
      css={isInverted ? styles.markdownInverted : styles.markdown}
    />
  );
}

function CitationModule(props: Props) {
  const { citations, renderers, containerBasedFullWidthOverride } = props;

  if (!citations) {
    return null;
  }

  const isInverted = isInvertedColor(props.backgroundColor ?? '');

  const contentPanel = () => (
    <Grid item container>
      <Grid item xs={12} md={6} css={containerBasedFullWidthOverride}>
        <Markdown source={citations} renderers={{ ...renderers }} isInverted={isInverted} />
      </Grid>
    </Grid>
  );

  const moduleLayoutConfig: ModuleLayoutConfig = getModuleLayoutConfig(props);

  return <ModuleLayout assembly={noImage(moduleLayoutConfig)} renderContent={contentPanel} />;
}

export default CitationModule;
